import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import ProductList from './components/ProductList';
import AddProductForm from './components/AddProductForm';
import RegisterSellerForm from './components/RegisterSellerForm';
import MarketIntel from './components/MarketIntel';
import ChatWindow from './components/ChatWindow';
import { ChatButton } from './components/ChatButton';
import ErrorBoundary from './components/ErrorBoundary';
import PWAInstaller from './components/PWAInstaller';
import PWAUpdater from './components/PWAUpdater';
import AdminDashboard from './components/AdminDashboard';
import ProductMap from './components/ProductMap';
import { useAppStore } from './store/useAppStore';
import { usePWA } from './hooks/usePWA';
import { api } from './services/api';
import type { Product } from './types';
import { PlusIcon } from './components/Icons';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { canInstall, promptInstall, isUpdateAvailable, applyUpdate } = usePWA();
  const { isChatOpen, isAuthModalOpen, setAuthModalOpen } = useAppStore();
  const [showInstaller, setShowInstaller] = useState(true);

  return (
    <div className="flex flex-col min-h-dvh">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-6 md:py-10">
        {children}
      </main>
      
      {/* MarketIntel in its original location with ChatButton attached to left */}
     <div className="w-full flex justify-end">
       <div className="relative">
         <MarketIntel />
         <ChatButton className="absolute right-full mr-3 top-1/2 -translate-y-1/2 z-50" />
       </div>
     </div> 
      <ChatWindow />

      {isAuthModalOpen && (
        <div className="fixed inset-0 z-[110] overflow-y-auto bg-black/50 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-md animate-in slide-in-from-bottom-8 duration-300">
            <RegisterSellerForm 
              onSuccess={() => setAuthModalOpen(false)} 
              onCancel={() => setAuthModalOpen(false)} 
            />
          </div>
        </div>
      )}

      {isUpdateAvailable && <PWAUpdater onUpdate={applyUpdate} />}
      {canInstall && showInstaller && (
        <PWAInstaller 
          onInstall={promptInstall} 
          onDismiss={() => setShowInstaller(false)} 
        />
      )}
      <footer className="bg-brand-brown-50 border-t border-divider text-app-text-secondary py-10 px-4 text-center">
        <div className="max-w-7xl mx-auto">
          <p className="text-xl font-black text-brand-green mb-4 tracking-tighter">AGRICONNECT</p>
          <p className="text-[10px] font-black uppercase tracking-[0.3em]">
            Sustainable Agriculture • Community Commerce • Data Efficient
          </p>
          <p className="text-[10px] mt-4">© {new Date().getFullYear()} AgriConnect. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

const Marketplace: React.FC = () => {
  const { seller, fetchProducts, addProduct, updateProduct, deleteProduct, toggleProductStatus, setAuthModalOpen } = useAppStore();
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formInitialData, setFormInitialData] = useState<Omit<Product, 'id'> | null>(null);
  const navigate = useNavigate();

  const handleSaveProduct = async (data: Omit<Product, 'id'>, blob?: Blob) => {
    if (editingProduct) {
      await updateProduct(editingProduct.id, data, blob);
    } else {
      await addProduct(data, blob);
    }
    setShowForm(false);
    setEditingProduct(null);
    setFormInitialData(null);
  };

  const handleAddClick = () => {
    if (!seller) setAuthModalOpen(true);
    else {
      setEditingProduct(null);
      setFormInitialData(null);
      setShowForm(true);
    }
  };

  const handleDuplicate = (product: Product) => {
    const { id, ...data } = product;
    setEditingProduct(null);
    setFormInitialData({ ...data, name: `${data.name} (Copy)` });
    setShowForm(true);
  };

  return (
    <div className="animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
        <div>
          <h2 className="text-3xl md:text-5xl font-black text-brand-green tracking-tight">Fresh Harvest</h2>
          <div className="flex items-center gap-4 mt-1">
             <p className="text-app-text-secondary font-bold uppercase text-xs tracking-widest leading-none">Direct from Suriname's farmers</p>
             <button onClick={() => navigate('/map')} className="text-[10px] font-black uppercase text-brand-green border-b border-brand-green cursor-pointer hover:text-brand-green-hover hover:border-brand-green-hover transition-colors">Switch to Map</button>
          </div>
        </div>
        <button 
          onClick={handleAddClick}
          className="bg-btn-primary text-btn-primary-text px-6 py-3 rounded-full font-black text-sm flex items-center gap-2 hover:bg-btn-primary-hover transition-all shadow-card-hover transform hover:scale-105 active:scale-95 w-full md:w-auto justify-center"
        >
          <PlusIcon className="w-5 h-5" />
          List Product
        </button>
      </div>

      <ProductList 
        onEdit={(p) => { setEditingProduct(p); setFormInitialData(p); setShowForm(true); }}
        onDuplicate={handleDuplicate}
        onDelete={deleteProduct}
        onToggleStatus={toggleProductStatus}
      />

      {showForm && (
        <div className="fixed inset-0 z-[70] overflow-y-auto bg-black/50 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-lg animate-in slide-in-from-bottom-8 duration-300">
            <AddProductForm 
              initialData={formInitialData}
              mode={editingProduct ? 'edit' : 'create'}
              onSave={handleSaveProduct} 
              onCancel={() => { setShowForm(false); setEditingProduct(null); setFormInitialData(null); }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const App: React.FC = () => {
  const { seller, setOnlineStatus, checkSession, fetchChatSessions } = useAppStore();

  useEffect(() => {
    checkSession();

    const handleStatus = () => {
      const status = navigator.onLine;
      setOnlineStatus(status);
      if (status) api.syncOfflineActions();
    };
    window.addEventListener('online', handleStatus);
    window.addEventListener('offline', handleStatus);
    
    // Refresh chat sessions on new message events
    const cleanupChat = api.onChatUpdate(() => {
      fetchChatSessions();
    });

    return () => {
      window.removeEventListener('online', handleStatus);
      window.removeEventListener('offline', handleStatus);
      cleanupChat();
    };
  }, [setOnlineStatus, checkSession, fetchChatSessions]);

  // Reactive fetch when seller changes (e.g. login)
  useEffect(() => {
    if (seller) {
      fetchChatSessions();
    }
  }, [seller, fetchChatSessions]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Marketplace />} />
            <Route path="/map" element={<ProductMap />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<Marketplace />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
